import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { getOperationKey, stringsEqualIgnoreCase } from '../util';
import { openNewPage, NavigationType } from '../util/navigationHelpers';

import PrimaryButton from '../ui/PrimaryButton';
import SecondaryButton from '../ui/SecondaryButton';
import WhiteButton from '../ui/WhiteButton';
import LightBlueButton from '../ui/LightBlueButton';

import { moveScreenToOperation, moveScreenToTop, pushScreenToStack, ScreenStackItem } from '../features/screenStackSlice';
import { moveJobToOperation, replaceJobMenuTimestamp } from '../features/jobMenuSlice';
import IfmsButton from '../ui/IfmsButton';
import useJobMenus from '../hooks/useJobMenus';

type UniqueLinkButtonProps = {
  link: string,
  viewLink?: string,
  operation?: string,
  label: React.ReactNode|string,
  beforeRedirect?: (() => Promise<boolean>)|undefined,
  uniqueLinkPrefixQuery?: ((x:ScreenStackItem) => boolean) | string | null,
  disabled?: boolean,
  display?: 'primary' | 'secondary' | 'white' | 'lightblue',
  baseClass?: string,
  [x:string]: any
};

function UniqueLinkButton({
  link,
  viewLink,
  operation,
  label,
  beforeRedirect = undefined,
  uniqueLinkPrefixQuery = undefined,
  disabled = false,
  display = 'primary',
  baseClass,
  goToAlreadyOpenPage = false,
  isMainOp = false,
  ...other }: UniqueLinkButtonProps) {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const jobMenu = useAppSelector(state => state.jobMenu);
  const screenStack = useAppSelector(x => x.screenStack);
  const operationState = useAppSelector(x => x.operation);
  const activeOperation = operationState.operation;
  const { moveToSingleOpenedScreenInAnyOperationStartingWithUrl } = useJobMenus();

  let activeOperationKey = getOperationKey(activeOperation);
  if (!activeOperationKey) {
    if (activeOperation === 'Reference Files' || activeOperation === 'Settings' || activeOperation === 'Utilities') {
      activeOperationKey = getOperationKey('Standalone');
    }
  }

  const isDisabled = disabled || ( !isMainOp && (
    !!activeOperationKey &&
    (
      !!uniqueLinkPrefixQuery
        ? (
          _.isString(uniqueLinkPrefixQuery)
            ? screenStack[activeOperationKey].some(x => x.url && x.url.toLowerCase().startsWith(uniqueLinkPrefixQuery.toLowerCase()))
            : screenStack[activeOperationKey].some(x => uniqueLinkPrefixQuery(x))
        )
        : screenStack[activeOperationKey].some(x => stringsEqualIgnoreCase(x.url, link))
    )
  ));

  function OpenNewPageAsync() {
    let urls = [link];
    if(viewLink) urls.push(viewLink);
    if (goToAlreadyOpenPage) {
      if(link.indexOf('/edit') > -1) urls.push(link.replace('/edit', '/view'));
      if(link.indexOf('/view') > -1) urls.push(link.replace('/view', '/edit'));
    }

    moveToSingleOpenedScreenInAnyOperationStartingWithUrl(link, urls)
    return Promise.resolve(true);
  }

  async function handleClick() {
    let continueRedirect = false;
    
    if (beforeRedirect) {
      continueRedirect = await beforeRedirect();
      if (!continueRedirect) {
        return;
      }
    }

    continueRedirect = await OpenNewPageAsync();
    if (!continueRedirect) {
      return;
    }

    if (viewLink) {
      const viewModeTimestamp = jobMenu[String(operation)].find(y => (y.url === viewLink))?.timestamp
      if (viewModeTimestamp)
        dispatch(replaceJobMenuTimestamp({ url: link, timestamp: viewModeTimestamp }));
    }

  }

  if (baseClass) {
    return (
      <IfmsButton {...other} baseClass={baseClass} onClick={handleClick} disabled={isDisabled}>{label}</IfmsButton>
    );
  } else {
    if (display === 'primary') {
      return (
        <PrimaryButton {...other} onClick={handleClick} disabled={isDisabled}>{label}</PrimaryButton>
      );
    } else if (display === 'white') {
      return (
        <WhiteButton {...other} onClick={handleClick} disabled={isDisabled}>{label}</WhiteButton>
      );
    } else if (display === 'lightblue') {
      return (
        <LightBlueButton {...other} onClick={handleClick} disabled={isDisabled}>{label}</LightBlueButton>
      );
    } else {
      return (
        <SecondaryButton {...other} onClick={handleClick} disabled={isDisabled}>{label}</SecondaryButton>
      );
    }
  }
}

UniqueLinkButton.defaultProps = {
  disabled: false,
  display: 'primary',
  size: 'regular',
  viewLink: '',
  operation: '',
  isMainOp: false,
};

UniqueLinkButton.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  link: PropTypes.string.isRequired,
  viewLink: PropTypes.string.isRequired,
  operation: PropTypes.string.isRequired,
  uniqueLinkPrefixQuery: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  beforeRedirect: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  display: PropTypes.oneOf(['primary', 'secondary', 'white', 'lightblue', 'secondary-light']),
  size: PropTypes.oneOf(['large', 'small', 'regular']),
  baseClass: PropTypes.string,
  goToAlreadyOpenPage: PropTypes.bool,
  isMainOp: PropTypes.bool
};

export default React.memo(UniqueLinkButton);
